import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyClassroom } from '@innovamat/glimmer-assets';
import {
  Button,
  Column,
  EmptyState,
  TableComponent,
  ToggleButton,
  Typography,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { type ClassroomTeacher } from '../../../../modules/teacher';

import { useTeacherUpdateReportsPreferenceMutation } from '../../../../hooks/use-teacher-update-reports-preference';
import { useUnlinkTeacher } from '../../../../hooks/use-unlink-teacher';

import { EllipsisCell } from '../../../../components/common-table-components/ellipsis-cell';
import { StatusCell } from '../../../../components/common-table-components/status-cell';
import { tableStyles } from '../../../../components/common-table-components/table-styles';

import { UnlinkTeacherModal } from '../unlink-teacher-confirm-modal';

import { CloudOffIcon } from '../../../../components/cloud-off-icon';
import { useClassroom } from '../../../../hooks/use-classroom';
import { ActionsCell } from './classroom-teachers-table.actions-cell';
import { useClassroomTeachersColumnsVisibility } from './hooks/use-classroom-teachers-columns-visibility';

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;

  > div {
    max-width: 800px;
  }
`;

type ClassroomTeachersProps = {
  classroomId: string;
  classroomName: string;
  isUpdatePeriod?: boolean;
  isPlatformEnabled: boolean;
  organizationId: string;
  teachers: ClassroomTeacher[];
  canLeave: (teacher: ClassroomTeacher) => boolean;
  shouldAllowAddingTeachersToClassroom: boolean;
  onAddTeachers: () => void;
};

function ClassroomTeachersTable({
  classroomId,
  classroomName,
  isUpdatePeriod = false,
  isPlatformEnabled,
  organizationId,
  teachers,
  canLeave,
  shouldAllowAddingTeachersToClassroom,
  onAddTeachers,
}: ClassroomTeachersProps): JSX.Element {
  const [isUnlinkTeacherModalOpen, setIsUnlinkTeacherModalOpen] =
    useState(false);
  const [teacherToUnlink, setTeacherToUnlink] = useState<ClassroomTeacher>();
  const { classroom } = useClassroom({ classroomId });

  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const theme = useGlimmerTheme();

  const handleUpdateReportPreference =
    useTeacherUpdateReportsPreferenceMutation({
      classroomId,
      isPlatformEnabled,
      organizationId,
      queryClient,
    });

  const { showEnrollmentStatusColumn, showIcon, getIconTooltip } =
    useClassroomTeachersColumnsVisibility({
      classroom,
      teachers,
    });

  const handleLeaveClass = (teacher: ClassroomTeacher): void => {
    setIsUnlinkTeacherModalOpen(true);
    setTeacherToUnlink(teacher);
  };

  const { isPending: isTeacherUnlinking, handleUnlinkTeacher } =
    useUnlinkTeacher({
      isPlatformEnabled,
      organizationId,
      onSuccess: () => setIsUnlinkTeacherModalOpen(false),
    });

  const columns: Column<ClassroomTeacher>[] = useMemo(() => {
    const enrollmentColumn: Column<ClassroomTeacher> = {
      id: 'enrollmentType' as any,
      label: '',
      width: 50,
      minWidth: '50px',
      render: (_, teacher) => {
        return showIcon(teacher) ? (
          <CloudOffIcon tooltipText={getIconTooltip(teacher)} />
        ) : null;
      },
    };

    return [
      ...(showEnrollmentStatusColumn ? [enrollmentColumn] : []),
      {
        id: 'firstName',
        label: t('common.firstName'),
        width: 150,
        minWidth: '150px',
        render: (_, teacher) => {
          return (
            <Typography.Subtitle2>
              <EllipsisCell
                text={teacher?.firstName ? teacher.firstName : '-'}
              />
            </Typography.Subtitle2>
          );
        },
      },
      {
        id: 'lastName',
        label: t('common.lastName'),
        width: 150,
        minWidth: '150px',
        render: (_, teacher) => {
          return (
            <Typography.Subtitle2>
              <EllipsisCell text={teacher?.lastName ? teacher.lastName : '-'} />
            </Typography.Subtitle2>
          );
        },
      },
      {
        id: 'email',
        label: t('common.email'),
        width: 300,
        minWidth: '300px',
        render: (_, teacher) => {
          return <EllipsisCell text={teacher?.email ? teacher.email : '-'} />;
        },
      },
      {
        id: 'reportsEnabled',
        label: t('teachers.receiveReports'),
        width: 100,
        minWidth: '100px',
        render: (_, teacher) => {
          return (
            <div style={{ display: 'flex', alignSelf: 'center' }}>
              <ToggleButton
                disabled={!teacher?.firstName && !teacher?.lastName}
                aria-label="receive reports toggle button"
                isActive={!!teacher?.reportsEnabled}
                onToggle={(isChecked) =>
                  handleUpdateReportPreference(isChecked, teacher.id)
                }
              />
            </div>
          );
        },
      },
      {
        id: 'lastLogin',
        label: t('common.status'),
        width: 150,
        minWidth: '150px',
        render: (_, teacher) => {
          return (
            <StatusCell
              expiresAt={teacher.expiresAt}
              isInvitation={teacher.isInvitation}
              isSchoolInvitation={teacher.isSchoolInvitation}
              lastLogin={teacher.lastLogin}
            />
          );
        },
      },
      {
        id: 'actions',
        width: 70,
        minWidth: '70px',
        sticky: true,
        stickyPosition: 'right',
        render: (_, teacher) => {
          return (
            <ActionsCell
              classroomId={classroomId}
              isPlatformEnabled={isPlatformEnabled}
              organizationId={organizationId}
              teacher={teacher}
              handleLeaveClass={handleLeaveClass}
              isLeaveDisabled={!canLeave(teacher)}
            />
          );
        },
      },
    ];
  }, [teachers, showEnrollmentStatusColumn, showIcon, getIconTooltip]);

  const hasTeachers = teachers.length > 0;

  return hasTeachers ? (
    <>
      <TableComponent
        isDisabled={isUpdatePeriod}
        backgroundColor="transparent"
        columns={columns}
        id="classroom-teachers-table"
        key={`teachers-table-${classroomId}`}
        rows={teachers}
        sx={tableStyles(theme)}
      />
      {isUnlinkTeacherModalOpen && (
        <UnlinkTeacherModal
          isLoading={isTeacherUnlinking}
          teacherName={teacherToUnlink?.firstName || ''}
          classroomName={classroomName}
          onConfirm={() =>
            handleUnlinkTeacher({ classroomId, teacher: teacherToUnlink! })
          }
          onCancel={() => setIsUnlinkTeacherModalOpen(false)}
        />
      )}
    </>
  ) : (
    <EmptyStateContainer>
      <EmptyState
        Image={EmptyClassroom}
        title={t('teachers.emptyState.title')}
        subtitle={t('teachers.emptyState.subtitle')}
        action={
          shouldAllowAddingTeachersToClassroom && (
            <Button onClick={onAddTeachers}>
              {t('teachers.buttons.addTeachers')}
            </Button>
          )
        }
      />
    </EmptyStateContainer>
  );
}

export { ClassroomTeachersTable };

